import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEnterpriseViewVersionHtml, getEnterpriseView } from '../services/EnterpriseAdminAccess';
import DesktopWarningBanner from '../components/DesktopWarningBanner';

const EnterpriseViewData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get state from navigation
  const { 
    enterpriseId, 
    viewId, 
    versionId, 
    viewTitle,
    projectTitle,
    userEmail,
    enterpriseName 
  } = location.state || {};
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [device, setDevice] = useState('Desktop'); // Default to Desktop

  // Function to inject custom styles into the HTML content
  const injectScrollbarStyles = useCallback((html) => {
    if (!html) return html;
    
    const scrollbarStyles = `
      <style>
        /* Custom scrollbar styles for better consistency */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        /* Firefox scrollbar styles */
        * {
          scrollbar-width: thin;
          scrollbar-color: #888 #f1f1f1;
        }
      </style>
    `;
    
    // Try to inject before </head>, fallback to before </body>
    if (html.includes('</head>')) {
      return html.replace('</head>', scrollbarStyles + '</head>');
    } else if (html.includes('</body>')) {
      return html.replace('</body>', scrollbarStyles + '</body>');
    } else {
      return scrollbarStyles + html;
    }
  }, []);

  // Load HTML content and view metadata
  const loadHtmlContent = useCallback(async () => {
    if (!enterpriseId || !viewId || !versionId) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch both view metadata and HTML content in parallel
      const [viewResponse, htmlResponse] = await Promise.all([
        getEnterpriseView(enterpriseId, viewId),
        getEnterpriseViewVersionHtml(enterpriseId, viewId, versionId)
      ]);
      
      // Handle view metadata response
      if (viewResponse && viewResponse._id) {
        // Extract device information from view data
        const viewDevice = viewResponse.device || 'Desktop';
        setDevice(viewDevice);
      }
      
      // Handle HTML content response
      if (htmlResponse.status === 'success' && htmlResponse.html) {
        setHtmlContent(htmlResponse.html);
      } else {
        setError('Failed to load view content');
      }
    } catch (err) {
      console.error('Error loading view content:', err);
      setError('Failed to load view content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId, viewId, versionId]);

  // Load content on component mount
  useEffect(() => {
    loadHtmlContent();
  }, [loadHtmlContent]);

  // Apply fullscreen styling to prevent scrollbars on main page
  useEffect(() => {
    // Prevent scrolling on body
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    // Cleanup when unmounted
    return () => {
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
    };
  }, []);

  // Handle navigation back
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading view content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <p className="text-lg font-semibold">Error</p>
          </div>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={handleBack}
            className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!htmlContent) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white">No content available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-black">
      <DesktopWarningBanner isDesktop={device === 'Desktop'} />
      {/* Header/Navbar similar to SharedContent */}
      <div className="bg-neutral-900 py-2 px-4 flex justify-between items-center">
        {/* Left side - Title and info */}
        <div className="flex flex-col">
          <div className="text-sm font-bold text-slate-300">
            {viewTitle || 'Enterprise View'}
          </div>
          <div className="text-xs text-slate-400">
            {projectTitle && `${projectTitle} • `}
            {enterpriseName && `${enterpriseName} • `}
            {userEmail}
          </div>
        </div>
        
        {/* Right side - Back button */}
        <div>
          <button
            onClick={handleBack}
            className="text-slate-300 hover:text-white px-3 py-1 rounded transition-colors text-sm"
          >
            ← Back
          </button>
        </div>
      </div>
      
      {/* Main content area - Handle mobile vs desktop like SharedContent.js */}
      {device === 'Desktop' ? (
        <div className="flex-1 bg-white">
          <iframe 
            title={`Enterprise View - ${viewTitle || 'Untitled View'}`}
            srcDoc={injectScrollbarStyles(htmlContent)}
            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
          />
        </div>
      ) : (
        <div className="flex-1 bg-black flex items-center justify-center">
          <div className="border rounded-md shadow-sm p-4 bg-white mx-auto w-[384px]">
            <iframe 
              title={`Enterprise View - ${viewTitle || 'Untitled View'}`}
              srcDoc={injectScrollbarStyles(htmlContent)}
              style={{ width: "100%", height: "80vh", border: "none" }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseViewData;